import { FC, useMemo } from 'react';
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  InputAdornment,
  Button,
} from '@mui/material';
import { IconSearch, IconFilter, IconX } from '@tabler/icons-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import useGetPartnerBoats from '../../../../hooks/useGetPartnerBoats';
import type { MyTripsQuery } from '../../../../gql/graphql';

interface TripFiltersProps {
  trips: MyTripsQuery['myTrips'];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  boatFilter: number | 'all';
  onBoatChange: (value: number | 'all') => void;
  routeFilter: number | 'all';
  onRouteChange: (value: number | 'all') => void;
  startDate: Dayjs | null;
  onStartDateChange: (value: Dayjs | null) => void;
  endDate: Dayjs | null;
  onEndDateChange: (value: Dayjs | null) => void;
  onClearAll?: () => void;
}

const TripFilters: FC<TripFiltersProps> = ({
  trips,
  searchQuery,
  onSearchChange,
  boatFilter,
  onBoatChange,
  routeFilter,
  onRouteChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onClearAll,
}) => {
  const { boats } = useGetPartnerBoats();

  // Extract unique routes from trips
  const uniqueRoutes = useMemo(() => {
    const routeMap = new Map<number, string>();
    trips.forEach(trip => {
      if (trip.routeId && trip.routeName) {
        routeMap.set(trip.routeId, trip.routeName);
      }
    });
    return Array.from(routeMap.entries()).map(([routeId, routeName]) => ({
      routeId,
      routeName,
    }));
  }, [trips]);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    boatFilter !== 'all' ||
    routeFilter !== 'all' ||
    startDate !== null ||
    endDate !== null;

  return (
    <Box className="mb-6 space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700">
          <IconFilter className="h-5 w-5" />
          <h3 className="text-sm font-semibold">Filters</h3>
          {hasActiveFilters && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Active
            </span>
          )}
        </div>
        {hasActiveFilters && onClearAll && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<IconX className="h-4 w-4" />}
            onClick={onClearAll}
            className="text-gray-600"
          >
            Clear All
          </Button>
        )}
      </div>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by trip ID, boat, route..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch className="h-5 w-5 text-gray-400" />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Boat Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Boat</InputLabel>
            <Select
              value={boatFilter}
              label="Boat"
              onChange={e => onBoatChange(e.target.value as number | 'all')}
            >
              <MenuItem value="all">All Boats</MenuItem>
              {boats.map(boat => (
                <MenuItem key={boat.boatId} value={boat.boatId}>
                  {boat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Route Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Route</InputLabel>
            <Select
              value={routeFilter}
              label="Route"
              onChange={e => onRouteChange(e.target.value as number | 'all')}
            >
              <MenuItem value="all">All Routes</MenuItem>
              {uniqueRoutes.map(route => (
                <MenuItem key={route.routeId} value={route.routeId}>
                  {route.routeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Range - Start Date */}
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={newValue => onStartDateChange(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
              },
            }}
          />

          {/* Date Range - End Date */}
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={newValue => onEndDateChange(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
              },
            }}
          />
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default TripFilters;
